import React, { useState, useEffect, useRef, useCallback } from 'react'
import cls from './Select.module.scss'
import { ArrowLeft } from '@Static/icons/DashboardIcons'
import { CheckIcon } from '@Static/icons/FooterIcons'

interface SelectOption {
  value: string
  label: string
  data?: any
}

interface SelectProps {
  value: string
  name: string
  title?: string
  options: SelectOption[]
  onChangeHandler: any
  onBlurHandler?: (name: string, value: string) => void
  onFocus?: (e: React.FocusEvent<HTMLDivElement>, name?: string) => void
  disabled?: boolean
  error?: boolean
  errorMessage?: string
  focus?: boolean
  className?: string
  placeholder?: string
}

const Select: React.FC<SelectProps> = ({
  value,
  name,
  title,
  options,
  onChangeHandler,
  onBlurHandler,
  onFocus,
  disabled = false,
  error = false,
  errorMessage,
  focus = false,
  className = '',
  placeholder,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedValue, setSelectedValue] = useState(value)
  const [selectedLabel, setSelectedLabel] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectRef = useRef<HTMLDivElement>(null)

  const findSelectedOption = useCallback(
    (val: string) => {
      return options.find((option) => option.value === val)
    },
    [options],
  )

  useEffect(() => {
    setSelectedValue(value)
    const option = findSelectedOption(value)
    setSelectedLabel(option?.label || placeholder || '')
  }, [value, options, findSelectedOption, placeholder])

  useEffect(() => {
    if (focus && selectRef.current) {
      selectRef.current.focus()
    }
  }, [focus])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        handleBlur()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleOptionClick = (option: SelectOption) => {
    setSelectedValue(option.value)
    setSelectedLabel(option.label)
    onChangeHandler(name, option.value, option.data)
    setIsOpen(false)
    handleBlur()
  }

  const handleSelectClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleBlur = () => {
    if (onBlurHandler) {
      onBlurHandler(name, selectedValue)
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    if (onFocus) {
      onFocus(e, name)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        setIsOpen(!isOpen)
        break
      case 'Escape':
        setIsOpen(false)
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'Tab':
        if (isOpen) {
          setIsOpen(false)
          handleBlur()
        }
        break
    }
  }

  const getWrapperClassName = () => {
    const classes = [cls.wrapper]
    if (error) classes.push(cls.error)
    if (disabled) classes.push(cls.disabled)
    if (className) classes.push(className)
    return classes.join(' ')
  }

  const getSelectContainerClassName = () => {
    const classes = [cls.selectContainer]
    if (error) classes.push(cls.error)
    if (isOpen) classes.push(cls.open)
    if (disabled) classes.push(cls.disabled)
    return classes.join(' ')
  }

  const selectedOption = findSelectedOption(selectedValue)
  const displayLabel = selectedOption?.label || selectedLabel

  return (
    <div className={getWrapperClassName()} ref={wrapperRef}>
      {title && (
        <div className={cls.label}>
          {title}
        </div>
      )}

      <div
        ref={selectRef}
        className={`${getSelectContainerClassName()} ${cls.select} ${error && cls.error}`}
        onClick={handleSelectClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={`${name}-dropdown`}
        aria-labelledby={`${name}-label`}
        id={`${name}-combobox`}
      >
        <div className={cls.selectedValue}>{displayLabel || placeholder || 'Choose'}</div>

        <div className={`${cls.arrow} ${isOpen ? cls.arrowOpen : ''}`}>
          <ArrowLeft />
        </div>

        {isOpen && !disabled && (
          <div className={cls.dropdown}>
            {placeholder && (
              <div
                className={`${cls.option} ${!selectedValue ? cls.optionSelected : ''}`}
                onClick={() => handleOptionClick({ value: '', label: placeholder })}
                role="option"
                aria-selected={!selectedValue}
              >
                {placeholder}
              </div>
            )}

            {options.map((option, index) => (
              <div
                key={`${option.value}-${index}`}
                className={`${cls.option} ${option.value === selectedValue ? cls.optionSelected : ''}`}
                onClick={() => handleOptionClick(option)}
                role="option"
                aria-selected={option.value === selectedValue}
              >
                {option.label}
                {option.value === selectedValue && <CheckIcon />}
              </div>
            ))}
          </div>
        )}
      </div>

      {error && errorMessage && <div className={cls.errorMessage}>{errorMessage}</div>}
    </div>
  )
}

export default Select
